import Button from 'tio-ui/components/button';
import { not, or } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Table from '../../components/comments/table.gts';
import type { CommentExtension as CommentModel } from 'tio-data/data/resources/comment.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { Store } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import { fn } from '@ember/helper';

export interface ApproverCommentsSignature {
  Args: {
    commentable: TasApplicationModel | TasProgramInstanceModel;
    commentableType: string;
    authoredByPerson: PersonModel;
    commentTags: string[];
  };
  Element: HTMLElement;
}

export type CommentsMap = {
  [key: string]: CommentModel[];
};

export default class ApproverCommentsComponent extends Component<ApproverCommentsSignature> {
  @service declare store: Store;
  @tracked isAddingComment = false;

  get viewingTasApplication() {
    return this.args.commentableType === 'TasApplication';
  }

  getInstanceAndApplicationComments(
    topLevelComments: CommentModel[],
    commentable: TasApplicationModel | TasProgramInstanceModel
  ): CommentsMap {
    const commentsMap: CommentsMap = {};
    commentsMap['programInstance'] = topLevelComments;

    if ('tasApplications' in commentable) {
      const tagsToInclude = this.args.commentTags ?? [];
      commentable.tasApplications.forEach((application: TasApplicationModel) => {
        // @ts-expect-error: Application id used as object key type mismatch
        commentsMap[application.id] = application.comments
          ? [...application.comments]
              .filter((comment) => comment.tags?.some((tag) => tagsToInclude.includes(tag)))
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          : [];
      });
    }

    return commentsMap;
  }

  get sortedComments() {
    const tagsToInclude = this.args.commentTags ?? [];
    const topLevelComments = this.args.commentable?.comments
      ? [...this.args.commentable.comments]
          .filter((comment) => comment.tags?.some((tag) => tagsToInclude.includes(tag)))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      : [];

    const commentsMap = !this.viewingTasApplication
      ? this.getInstanceAndApplicationComments(topLevelComments, this.args.commentable)
      : { application: topLevelComments };

    return commentsMap;
  }

  get totalCommentsLength(): number {
    return Object.values(this.sortedComments).reduce(
      (total, comments) => total + comments.length,
      0
    );
  }

  get isPartnerMessagingComments(): boolean {
    return this.args.commentTags?.includes('Partner Messaging') ?? false;
  }

  addComment = () => {
    this.isAddingComment = true;
    const commentable = this.args.commentable;
    const commentableType = this.args.commentableType;
    const authorPerson = this.args.authoredByPerson;
    const tags = this.args.commentTags || [];

    return this.store.createRecord('comment', {
      commentable: commentable,
      commentableType: commentableType,
      createdAt: new Date().toISOString(),
      comment: '',
      person: authorPerson,
      tags: tags,
    });
  };

  toggle = (key: string) => {
    // @ts-expect-error: make a helper eventually
    this[key] = !this[key];
  };

  <template>
    <Section>
      <:header>
        {{#if this.isPartnerMessagingComments}}
          <h2 class="tio-text-heading">
            {{t "common.tuition_assistance.employee_comments"}}
            ({{this.totalCommentsLength}})
          </h2>
          <p class="italic">{{t "common.tuition_assistance.employee_comments_description"}}</p>
        {{else}}
          <h2 class="tio-text-heading">
            {{t "common.tuition_assistance.approver_comments"}}
            ({{this.totalCommentsLength}})
          </h2>
          <p class="italic">{{t "common.tuition_assistance.approver_comments_description"}}</p>
        {{/if}}
      </:header>
      <:body>
        <div class="tio-flex-between grow flex-col">
          <div class="grow">
            <Table
              @model={{this.sortedComments}}
              @currentPerson={{@authoredByPerson}}
              @onClose={{fn this.toggle "isAddingComment" this}}
            />
          </div>

          <div class="self-center">
            <Button
              @intent="primary"
              {{on "click" this.addComment}}
              disabled={{or (not @commentable) this.isAddingComment}}
              class="w-48 my-4 !static"
              data-test-add-comment-button
            >
              {{t "common.add_comment"}}
            </Button>
          </div>
        </div>
      </:body>
    </Section>
  </template>
}
